// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import '../../../styles/signature.css';

const canvasWidth = 2500;
const pencilWidth = 2.4;
const eraserWidth = 80;
const fontSize = 12; // pt

export default class Canvas extends React.Component {

    // Non-state variables - do not need to re-render DOM
    canvas = null;
    ctx = null;
    container = null;
    mouseIsDown = false;

    canvasX = null;
    canvasY = null;

    hoverX = null;
    hoverY = null;
    
    textbox = null;
    textboxX = null;
    textboxY = null;

    labelboxX1 = null;
    labelboxY1 = null;
    labelboxX2 = null;
    labelboxY2 = null;

    dragX = null;
    dragY = null;

    componentDidMount = () => {

        // References
        this.canvas = this.refs["page-canvas-"+this.props.pageIdx];
        this.container = this.refs["page-container-"+this.props.pageIdx];
        this.signature = this.refs["signature-editor-"+this.props.pageIdx];

        // Load canvas
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.width = canvasWidth;
        this.canvas.height = canvasWidth;
        this.ctx = this.canvas.getContext('2d');
        this.ctx.lineWidth = pencilWidth;

        // Load existsing data
        this.loadDataURL(this.props.initialData, this.props.pageData);
        
        document.addEventListener('dragover', e => {
            this.dragX = e.pageX;
            this.dragY = e.pageY;
        })
    }

    initialData = null
    componentWillReceiveProps = (next) => {
        if ((next.initialData && this.props.initialData !== next.initialData) || (next.pageData && this.props.pageData !== next.pageData))
            this.loadDataURL(next.initialData, next.pageData);

        if (this.props.tool !== next.tool && this.props.tool === "Textbox")
            this.bakeTextbox();

        // Adjust any relational elements if zoom changes.
        if (this.textbox && next.zoom && this.props.zoom && next.zoom !== this.props.zoom) {
            // Recompute textbox position
            const scale = next.zoom / this.props.zoom;
            this.textboxX *= scale;
            this.textboxY *= scale;
            this.fontSize *= scale;

            this.textbox.style.top = this.textboxY+"px";
            this.textbox.style.left = this.textboxX+"px";
            this.textbox.style.height = this.fontSize+"px";
            this.textbox.style.width = "calc(100% - " + (10 + this.textboxX).toFixed(2) + "px)";
            this.textbox.style.lineHeight = this.fontSize+"px";
            this.textbox.style.fontSize = this.fontSize+"px";
        }
    }

    loadDataURL = (dataUrl, pageData) => {
        if (!dataUrl || !pageData)
            return;

        const page = pageData.getBoundingClientRect();

        if (!page || !page.height || !page.width)
            return setTimeout(() => this.loadDataURL(dataUrl, pageData), 250); // Try again once the page has loaded

        this.canvas.height = page.height * canvasWidth / page.width;
        const img = new Image();
        img.onload = () => this.ctx.drawImage(img,0,0);
        img.src = dataUrl;
        this.initialData = dataUrl;
        this.props.setCanvas(this.canvas);
    }

    clearTextbox = () => {
        if (!this.textbox) return;
        this.textbox.remove();
        this.textbox = null;
        this.hoverX = null;
        this.hoverY = null;
        this.props.setTool('');
    }

    bakeTextbox = () => {
        if (!this.textbox) return;
        const rect = this.container.getBoundingClientRect();
        const scaleW = this.canvas.width / rect.width;
        const scaleH = this.canvas.height / rect.height;
        const scaledFontSize = fontSize * this.canvas.height / 792; // 792 px is standard for 1px = 1pt
        this.ctx.font = String(scaledFontSize) + 'px sans-serif';
        this.ctx.fillStyle = this.props.color;
        this.ctx.textBaseline = "top";
        this.ctx.fillText(this.textbox.value, this.textboxX * scaleW, this.textboxY * scaleH);
        this.textbox.remove(); // Clear temporary textbox
        this.textbox = null;
        this.hoverX = null;
        this.hoverY = null;
        this.props.setTool('');
        this.props.save(this.canvas);
    }

    bakeSignature = () => {
        if (!this.signature) return;
        const rect = this.signature.getBoundingClientRect();

        // Compute canvas coordinates
        const coords = this.pageToCanvasCoords(rect);

        let signature = new Image();
        signature.onload = () => this.ctx.drawImage(signature, coords.left, coords.top, coords.width, coords.height);
        signature.src = this.props.signature;
        this.signature.style.display = 'none'; // Hide editable

        this.props.save(this.canvas); // Save
    }

    pageToCanvasCoords = (rect) => {
        const container = this.container.getBoundingClientRect();
        return {
            left: (rect.left - container.left) * this.canvas.width / container.width,
            top: (rect.top - container.top) * this.canvas.height / container.height,
            width: rect.width * this.canvas.width / container.width,
            height: rect.height * this.canvas.height / container.height
        };
    }

    setFontSize = (X, Y) => {
        const rect = this.container.getBoundingClientRect();
        if (!rect || !this.props.words) return;
        
        const defaultFontSize = fontSize * rect.height / 792;
        // 792 px is standard for 1px = 1pt

        const x = X / rect.width;
        const y = Y / rect.height;

        // If there are words before, then line up with that.
        // Compile all words directly to the left of the marker
        const line = this.props.words.filter(w => {
            // Required properties
            if (!w.x || !w.y || !w.text || !w.width || !w.height)
                return false;

            // On the same line
            if (y > w.y + 1.5*w.height || y < w.y - 0.5*w.height)
                return false;

            // Within a quarter page to the left
            const right = w.x + w.width;
            if (x < right || x > right + 0.25)
                return false;

            // We're good so far!
            return true;
        });

        let f=0, weight=0;
        for (const w of line) {
            // Alpha-numeric Character types based on height:
            // Full-height characters
            if (w.text.match(/[jQ|]/g)) {
                weight += 1;
                f += w.height;
                continue;
            }

            // Ascent characters
            if (w.text.match(/[ABCDEFGHIJKLMNOPRSTUVWXYZbdfhiklt1234567890\\/]/g)) {
                if (w.text.match(/[gpqy]/g)) { // Descent characters
                    weight += 1;
                    f += w.height;
                }

                else { // Ascent only
                    weight += 0.7;
                    f += 0.7 * (w.height * 1.4);
                }

                continue;
            }

            // Only small characters
            if (w.text.match(/^[acemnorsuvwxz=.:]*$/g)) {
                weight += 0.4;
                f += 0.4 * (w.height * 2.2);
                continue
            }

            // Ignore weird words
            
        }

        if (weight > 0) {
            f = f / weight * rect.height;
            this.fontSize = f;
        }

        else this.fontSize = defaultFontSize;
    }

    updateCanvasXY = (e, dragged=false) => {
        const page = this.props.pageData.getBoundingClientRect();
        if (Math.abs(this.canvas.height/this.canvas.width - page.height/page.width) > 0.01)
            this.canvas.height = page.height * canvasWidth / page.width; 

        const rect = this.container.getBoundingClientRect();
        // this.canvas.width = rect.width;
        // this.canvas.height = rect.height;

        if (dragged && (!this.dragX || !this.dragY)) return;

        this.canvasX = ((dragged ? this.dragX : e.pageX) - rect.left) * this.canvas.width / rect.width;
        this.canvasY = ((dragged ? this.dragY : e.pageY) - rect.top) * this.canvas.height / rect.height;
    }

    updateTextboxXY = (e, hover=false) => {
        const rect = this.container.getBoundingClientRect();
        const x = (e.pageX - rect.left) + 2;
        const y = (e.pageY - rect.top) - this.fontSize / 2 + 1;
        // this.setFontSize(x,y);
        this.fontSize = fontSize * rect.height / 792;

        if (hover) {
            this.hoverX = x;
            this.hoverY = y;
        } else {
            this.textboxX = x;
            this.textboxY = y;
        }
    }

    mouseDown = async (e) => {
        if (this.textbox) this.bakeTextbox();
        this.mouseIsDown = true;
        this.updateCanvasXY(e);

        this.ctx.globalCompositeOperation = this.props.tool === 'Eraser' ? 'destination-out' : 'source-over';

        if (this.props.tool === 'Pencil') {
            this.ctx.lineWidth = pencilWidth;
            this.ctx.strokeStyle = this.props.color;
        }

        else if (this.props.tool === 'Eraser') {
            this.ctx.lineWidth = eraserWidth;
        }

        else if (this.props.tool === 'InsertBox' || this.props.tool === 'InsertInput') {
            const rect = this.container.getBoundingClientRect();
            this.labelboxX1 = this.canvasX - 2 * this.canvas.width / rect.width;
            this.labelboxY1 = this.canvasY - 2 * this.canvas.height / rect.height;
        }

        else if (this.props.tool === 'CopyBox') {
            this.props.pasteBound(
                this.canvasX / this.canvas.width,
                this.canvasY / this.canvas.height
            );
        }

        this.ctx.beginPath();
        this.ctx.moveTo(this.canvasX, this.canvasY);
    }

    mouseMove = (e) => {
        if (this.props.tool === 'Textbox')
            this.updateTextboxXY(e, true);

        if (!this.mouseIsDown) return;
        this.updateCanvasXY(e);
        if (['Pencil', 'Eraser'].includes(this.props.tool)) {
            this.ctx.lineCap = 'round';
            this.ctx.lineTo(this.canvasX, this.canvasY);
            this.ctx.stroke();
            this.ctx.fillStyle = this.props.color;
        }

        else if (this.props.tool === 'InsertBox' || this.props.tool === 'InsertInput') {
            const rect = this.container.getBoundingClientRect();
            this.labelboxX2 = this.canvasX - 2 * this.canvas.width / rect.width;
            this.labelboxY2 = this.canvasY - 2 * this.canvas.height / rect.height;
        }
    }

    mouseUp = (e) => {
        this.mouseIsDown = false;
        if (this.props.tool === 'Textbox') {
            if (this.textbox) this.textbox.remove();

            this.updateTextboxXY(e);

            // Create temporary textbox
            this.textbox = document.createElement("input");
            this.textbox.type = "text";
            this.textbox.style.backgroundColor = "transparent";
            this.textbox.style.border = "none";
            this.textbox.style.boxShadow = "none";
            this.textbox.style.borderRadius = "0";
            this.textbox.style.padding = "0";
            this.textbox.style.margin = "0";
            this.textbox.style.height = this.fontSize+"px";
            this.textbox.style.width = "calc(100% - " + (10 + this.textboxX).toFixed(2) + "px)";
            this.textbox.style.lineHeight = this.fontSize+"px";
            this.textbox.style.fontSize = this.fontSize+"px";
            this.textbox.style.caretColor = 'black';
            this.textbox.style.position = "absolute";
            this.textbox.style.fontFamily = "sans-serif";
            this.textbox.style.color = this.props.color;
            this.textbox.style.top = this.textboxY+"px";
            this.textbox.style.left = this.textboxX+"px";
            this.textbox.onkeydown = (e) => {
                if (e.keyCode === 13) return this.bakeTextbox();
                if (e.keyCode === 27) return this.clearTextbox();
            }
            this.container.appendChild(this.textbox);
            this.textbox.focus();
        }

        else if (this.props.tool === 'Sign') {
            this.updateCanvasXY(e);
            const rect = this.container.getBoundingClientRect();
            const scale = rect.width / this.canvas.width;
            this.signature.style.top = ((this.canvasY - 52)*scale)+"px";
            this.signature.style.left = ((this.canvasX - 142)*scale)+"px";
            this.signature.style.right = ((this.canvas.width - this.canvasX - 143)*scale)+"px";
            this.signature.style.bottom = ((this.canvas.height - this.canvasY - 53)*scale)+"px";

            // Make visible
            this.signature.style.display = 'block';
        }

        else if (this.props.tool === 'InsertBox' || this.props.tool === 'InsertInput') {
            if (!this.labelboxX1 || !this.labelboxX2 || !this.labelboxY1 || !this.labelboxY2)
                return;

            const x = Math.min(this.labelboxX1, this.labelboxX2) / this.canvas.width;
            const y = Math.min(this.labelboxY1, this.labelboxY2) / this.canvas.height;
            const width = Math.abs(this.labelboxX2 - this.labelboxX1) / this.canvas.width;
            const height = Math.abs(this.labelboxY2 - this.labelboxY1) / this.canvas.height;

            if (this.props.tool === 'InsertBox')
                this.props.insertBound(x, y, width, height);

            else this.props.callback(this.props.pageIdx, x, y, width, height);
            
            // Clear
            this.labelboxX1 = 0;
            this.labelboxY1 = 0;
            this.labelboxX2 = 0;
            this.labelboxY2 = 0;
        }

        if (['Pencil', 'Eraser', 'Textbox'].includes(this.props.tool))
            this.props.save(this.canvas);
    }

    dragSignatureCorner = (corner, e) => {
        e.preventDefault();
        e.stopPropagation();

        // Prevent jumps
        this.prevX = this.canvasX;
        this.prevY = this.canvasY;
        this.updateCanvasXY(e, true);

        const dx = this.canvasX - this.prevX;
        const dy = this.canvasY - this.prevY;

        if (Math.abs(dx) > 80 || Math.abs(dy) > 80)
            return;
        
        const rect = this.container.getBoundingClientRect();
        const scale = rect.width / this.canvas.width;

        // Control each side independently
        for (const side of ['top', 'bottom', 'left', 'right']) {
            let dimension = ['left', 'right'].includes(side) ? this.canvasX : this.canvasY;
            if (side === 'bottom')
                dimension = this.canvas.height - dimension;
            if (side === 'right')
                dimension = this.canvas.width - dimension;

            if (corner.includes(side))
                this.signature.style[side] = (dimension*scale)+"px";
        }
    }

    dragSignature = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Prevent jumps
        this.prevX = this.canvasX;
        this.prevY = this.canvasY;
        this.updateCanvasXY(e, true);
        
        const dx = this.canvasX - this.prevX;
        const dy = this.canvasY - this.prevY;

        if (Math.abs(dx) > 80 || Math.abs(dy) > 80)
            return;

        const rect = this.container.getBoundingClientRect();
        const scale = rect.width / this.canvas.width;

        // Move all four corners
        this.signature.style.top = (parseFloat(this.signature.style.top)+dy*scale)+"px";
        this.signature.style.bottom = (parseFloat(this.signature.style.bottom)-dy*scale)+"px";
        this.signature.style.left = (parseFloat(this.signature.style.left)+dx*scale)+"px";
        this.signature.style.right = (parseFloat(this.signature.style.right)-dx*scale)+"px";
    }

    scaleCanvasToPercent = (val, mode="x") => (100 * val / (mode === "x" ? this.canvas.width : this.canvas.height))

    renderBox = () => {
        if (!['InsertBox', 'InsertInput'].includes(this.props.tool) || !this.labelboxX1 || !this.labelboxX2 || !this.labelboxY1 || !this.labelboxY2)
            return <></>;
        return <div
            className="document-insert-box"
            style={{
                left: String(this.scaleCanvasToPercent(Math.min(this.labelboxX1, this.labelboxX2))) + "%",
                top: String(this.scaleCanvasToPercent(Math.min(this.labelboxY1, this.labelboxY2), "y")) + "%",
                width: String(this.scaleCanvasToPercent(Math.abs(this.labelboxX2 - this.labelboxX1))) + "%",
                height: String(this.scaleCanvasToPercent(Math.abs(this.labelboxY2 - this.labelboxY1), "y")) + "%"
            }}
        />
    }

    renderTextboxHover = () => {
        if (this.props.tool !== 'Textbox' || this.textbox || !this.fontSize || !this.hoverX || !this.hoverY) return <></>;
        return <div
            className="document-textbox-hover"
            style={{
                fontSize: this.fontSize+"px",
                height: this.fontSize+"px",
                lineHeight: this.fontSize+"px",
                top: this.hoverY+"px",
                left: this.hoverX+"px",
                color: this.props.color
            }}
        >Text goes here...</div>
    }

    render = () => {
        return (<>
            <div
                ref={"page-container-"+this.props.pageIdx} 
                className="document-canvas"
                style={{
                    pointerEvents: this.props.tool === "" || this.props.tool === "Select" ? "none" : "all"
                }}
            >
                <canvas 
                    ref={"page-canvas-"+this.props.pageIdx} 
                    onMouseDown={this.mouseDown} 
                    onMouseMove={this.mouseMove}
                    onMouseUp={this.mouseUp}
                    style={{
                        pointerEvents: this.props.tool === "" || this.props.tool === "Select" ? "none" : "all"
                    }}
                />
                {/* Signature editor */}
                <div
                    ref={"signature-editor-"+this.props.pageIdx}
                    className="signature-editor"
                    style={{
                        backgroundImage: "url(" + this.props.signature + ")",
                        cursor: "move"
                    }}
                    onDrag={e => this.dragSignature(e)}
                    onDragStart={e => {
                        const ghost = new Image();
                        ghost.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
                        e.dataTransfer.setDragImage(ghost, 0, 0);
                    }}
                    draggable={true}
                >
                    <div className="signature-editor-controls">
                        {/* Draggable corners */}
                        <div
                            onDrag={e => this.dragSignatureCorner('top-left', e)} 
                            className="signature-editor-corner signature-editor-corner-top-left"
                            draggable={true}
                        />
                        <div
                            onDrag={e => this.dragSignatureCorner('top-right', e)} 
                            className="signature-editor-corner signature-editor-corner-top-right"
                            draggable={true}
                        />
                        <div
                            onDrag={e => this.dragSignatureCorner('bottom-left', e)} 
                            className="signature-editor-corner signature-editor-corner-bottom-left"
                            draggable={true}
                        />
                        <div
                            onDrag={e => this.dragSignatureCorner('bottom-right', e)} 
                            className="signature-editor-corner signature-editor-corner-bottom-right"
                            draggable={true}
                        />

                        {/* Clear */}
                        <img
                            className="signature-editor-control-clear"
                            src="https://static.stax.ai/images/icons/remove.png"
                            alt="(-)"
                            title="Clear"
                            onClick={() => this.signature.style.display = 'none'}
                        />

                        {/* Confirm/bake */}
                        <img
                            className="signature-editor-control-bake"
                            src="https://static.stax.ai/images/icons/reviewed.png"
                            alt="(+)"
                            title="Confirm"
                            onClick={() => this.bakeSignature()}
                        />
                    </div>
                </div>

                {this.renderBox()}
                {this.renderTextboxHover()}
            </div>
        </>);
    }

};